import { useState } from "react"
import { Link } from "@inertiajs/react"
import { toast } from "sonner"
import {
  ArrowRight,
  Check,
  Copy,
  DollarSign,
  Gift,
  History,
  Link2,
  Mail,
  Share2,
  ShoppingCart,
  Sparkles,
  Users,
} from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Avatar } from "@/components/tuw/Avatar"

// Atrium item 7 — the customer Rewards & referrals hub: a net-new presentation
// of REAL reward/referral data (CustomerRewardsPresenter). Every figure is
// prop-driven — points, dollar worth, the earn rate (server-sourced from
// RewardPoint.points_per_dollar_spent, honouring the reward-points multiplier),
// the discount / bounty copy — nothing is hardcoded here (P-001). The page has
// no server mutations: redemption happens in the order's Pay Now step (where the
// customer chooses how many points to apply before checkout), so the CTA is a
// LINK into Post a Project and the only interactions here are client-side sharing.
//
// The new-customer variant is gated per-section on real data (empty hero when
// the balance is zero, empty activity when the ledger is empty, empty friends
// when there are no referrals) — more honest with partial data than the
// prototype's single toggle, and visually identical in the fully-empty case. The
// referral card always renders.

const POST_PROJECT_HREF = "/post"

function formatPoints(n) {
  return Number(n ?? 0).toLocaleString("en-US")
}

function formatMoney(value) {
  return `$${Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

// 200 pts = $1 — the dollar value of a redemption row, for the row's small note.
function pointsToMoney(points) {
  return formatMoney(Math.abs(Number(points ?? 0)) / 200)
}

function formatDate(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

// Classify a ledger row from its real fields (no icon column server-side): a
// deduction is a redemption; a referral-sourced addition reads off the reason
// string RedeemCustomerReferralJob writes; everything else is an order earn.
function rowKind(row) {
  if (row.deduction) return "redeem"
  if (/referral/i.test(row.reason ?? "")) return "referral"
  return "earn"
}

const ROW_ICON = { redeem: DollarSign, referral: Users, earn: ShoppingCart }

function BalanceHero({ balance, lifetimeEarned, redeemed }) {
  const empty = Number(balance?.points ?? 0) === 0 && Number(lifetimeEarned ?? 0) === 0

  // P8-H7 "Muster" — `.rw-hero` is 1-col at ≤700 and the ledger pane swaps its
  // left border for a top one (design/spec/account/rewards.html
  // `@media (max-width:700px)`). The border pattern below was already right, at
  // the wrong edge: `sm:` is `width >= 640`, so 641–700 kept the two-column hero
  // the design rules to one. `min-[701px]` compiles to `@media (width >= 701px)`.
  return (
    <div
      data-testid="rewards-hero"
      className="grid grid-cols-1 overflow-hidden rounded-2xl border border-border bg-card min-[701px]:grid-cols-[1.35fr_1fr]"
    >
      <div className="px-[26px] py-6">
        <div className="flex items-center gap-[7px] text-[11.5px] font-bold tracking-[0.08em] text-ink-3 uppercase">
          <Gift className="size-[15px] text-primary" strokeWidth={2} />
          Your Dibbly Rewards balance
        </div>

        <div className="mt-[9px] flex items-baseline gap-[9px]">
          <b className="font-display text-[46px] leading-none font-bold tracking-[-1.5px] text-foreground">
            {formatPoints(balance?.points)}
          </b>
          <span className="text-[15px] font-semibold text-ink-3">points</span>
        </div>

        <p className="mt-[11px] text-[14px] text-muted-foreground">
          {empty ? (
            <>
              Worth <b className="font-bold text-foreground">$0.00</b> — start earning below.
            </>
          ) : (
            <>
              Worth <b className="font-bold text-foreground">{formatMoney(balance?.dollarsValue)}</b> toward your next
              project
            </>
          )}
        </p>

        <span className="mt-[14px] inline-flex items-center gap-[6px] rounded-full bg-[color-mix(in_oklab,var(--done)_12%,transparent)] px-[11px] py-[5px] text-[11.5px] font-bold text-done">
          <Check className="size-[13px]" strokeWidth={2.4} />
          200 points = $1 · apply in the Pay Now step
        </span>
      </div>

      <div
        data-testid="rewards-ledger"
        className="flex flex-col justify-center gap-[14px] border-t border-border bg-accent px-[26px] py-6 min-[701px]:border-t-0 min-[701px]:border-l"
      >
        <div className="flex items-center justify-between">
          <span className="text-[12.5px] font-medium text-muted-foreground">Lifetime earned</span>
          <span className="font-display text-base font-bold text-foreground">{formatPoints(lifetimeEarned)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[12.5px] font-medium text-muted-foreground">Redeemed</span>
          <span className="font-display text-base font-bold text-foreground">{formatPoints(redeemed)}</span>
        </div>

        <Link
          href={POST_PROJECT_HREF}
          className="mt-1 inline-flex w-full items-center justify-center gap-[7px] rounded-[10px] bg-primary px-3 py-[11px] text-[13px] font-bold text-white no-underline transition-colors hover:bg-red-dark"
        >
          {empty ? "Start a project" : "Start a new project"}
          <ArrowRight className="size-[15px]" strokeWidth={2.3} />
        </Link>
        <p className="text-center text-[11px] leading-[1.4] text-ink-3">
          {empty
            ? "Earn your first points on your next order."
            : "Apply your points in the Pay Now step of any unpaid order."}
        </p>
      </div>
    </div>
  )
}

function HowYouEarn({ pointsPerDollar }) {
  const rate = Number(pointsPerDollar ?? 2)

  const steps = [
    {
      n: 1,
      title: "Place & pay for an order",
      body: (
        <>
          Every completed order earns <b className="font-bold text-foreground">{rate} points for every $1</b> you spend.
        </>
      ),
    },
    {
      n: 2,
      title: "Points bank automatically",
      body: "Points land in your balance once payment clears — nothing to claim.",
    },
    {
      n: 3,
      title: "Redeem in the Pay Now step",
      body: (
        <>
          <b className="font-bold text-foreground">200 points = $1 off</b>. Choose how many points to apply when you pay
          for an unpaid order.
        </>
      ),
    },
  ]

  // `.rw-how` — the same ruled ≤700 edge as the hero above.
  return (
    <div data-testid="rewards-how" className="grid grid-cols-1 gap-3 min-[701px]:grid-cols-3">
      {steps.map((step) => (
        <div key={step.n} className="rounded-[13px] border border-border bg-card px-[17px] py-4">
          <div className="mb-[11px] grid size-[26px] place-items-center rounded-lg bg-[var(--red-soft)] font-display text-[13px] font-bold text-primary">
            {step.n}
          </div>
          <h4 className="font-display text-[13.5px] font-bold tracking-[-0.2px] text-foreground">{step.title}</h4>
          <p className="mt-1 text-[12px] leading-[1.5] text-muted-foreground">{step.body}</p>
        </div>
      ))}
    </div>
  )
}

function CardHead({ icon: Icon, title, meta }) {
  return (
    <div className="flex items-center justify-between border-b border-border px-5 py-[15px]">
      <h3 className="flex items-center gap-[9px] font-display text-[14.5px] font-bold text-foreground">
        <Icon className="size-[17px] text-primary" strokeWidth={1.9} />
        {title}
      </h3>
      {meta && <span className="text-[11.5px] font-semibold text-ink-3">{meta}</span>}
    </div>
  )
}

function EmptyBlock({ icon: Icon, title, body }) {
  return (
    <div className="flex flex-col items-center px-6 py-10 text-center">
      <span className="mb-[13px] grid size-[46px] place-items-center rounded-[13px] border border-border bg-secondary text-ink-3">
        <Icon className="size-[22px]" strokeWidth={1.8} />
      </span>
      <h4 className="font-display text-[14px] font-bold text-foreground">{title}</h4>
      <p className="mt-[5px] max-w-[300px] text-[12.5px] leading-[1.5] text-ink-3">{body}</p>
    </div>
  )
}

function ActivityRow({ row }) {
  const kind = rowKind(row)
  const Icon = ROW_ICON[kind]
  const sign = row.deduction ? "−" : "+"

  // P8-H7 "Muster" — the ledger row takes the receipt row's two-line stack inside
  // the starved 861–1023 band. In a ~337px column the reason clipped to
  // "Redeemed at checko…", losing the trailing "on Order #10588" — the only thing
  // that says WHICH order the points came from. Reason on line 1, icon + amount
  // beneath it.
  return (
    <div
      data-testid="activity-row"
      className="flex items-center gap-[14px] border-b border-border px-5 py-[13px] last:border-b-0 min-[861px]:max-[1024px]:flex-wrap min-[861px]:max-[1024px]:gap-y-[6px]"
    >
      <span
        className={
          kind === "referral"
            ? "grid size-[34px] shrink-0 place-items-center rounded-[9px] bg-[color-mix(in_oklab,var(--chart-4)_12%,transparent)]"
            : "grid size-[34px] shrink-0 place-items-center rounded-[9px] bg-secondary"
        }
      >
        <Icon
          className={kind === "referral" ? "size-4 text-chart-4" : "size-4 text-ink-3"}
          strokeWidth={2}
        />
      </span>

      <div className="min-w-0 flex-1 min-[861px]:max-[1024px]:-order-1 min-[861px]:max-[1024px]:basis-full">
        <div data-testid="activity-reason" className="truncate text-[13.5px] font-semibold text-foreground min-[861px]:max-[1024px]:overflow-visible min-[861px]:max-[1024px]:text-clip min-[861px]:max-[1024px]:whitespace-normal">
          {row.reason}
        </div>
        <div className="mt-[2px] text-[11.5px] text-ink-3">{formatDate(row.createdAt)}</div>
      </div>

      <div
        data-testid="activity-amount"
        className={
          row.deduction
            ? "shrink-0 text-right font-display text-[15px] font-bold text-ink-3 min-[861px]:max-[1024px]:order-2 min-[861px]:max-[1024px]:ml-auto"
            : "shrink-0 text-right font-display text-[15px] font-bold text-done min-[861px]:max-[1024px]:order-2 min-[861px]:max-[1024px]:ml-auto"
        }
      >
        {sign}
        {formatPoints(row.points)}
        {row.deduction && (
          <small className="mt-px block text-[11px] font-semibold text-ink-3">−{pointsToMoney(row.points)}</small>
        )}
      </div>
    </div>
  )
}

function ActivityCard({ activity }) {
  const empty = activity.length === 0

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <CardHead icon={History} title="Points activity" meta={empty ? null : "Last 90 days"} />
      {empty ? (
        <EmptyBlock
          icon={History}
          title="No activity yet"
          body="Your points history will appear here once you place your first order."
        />
      ) : (
        activity.map((row, i) => <ActivityRow key={`${row.createdAt}-${i}`} row={row} />)
      )}
    </div>
  )
}

function ReferralCard({ referral, referralEarned, onToast }) {
  const [copied, setCopied] = useState(false)
  const link = referral?.link

  function copyLink(message) {
    if (!link) return
    navigator.clipboard?.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
    onToast(message)
  }

  function shareLink() {
    if (!link) return
    if (navigator.share) {
      navigator
        .share({ title: "Join me on Dibbly", url: link })
        .catch(() => {})
    } else {
      copyLink("Referral link copied to share")
    }
  }

  const mailto = link
    ? `mailto:?subject=${encodeURIComponent("Join me on Dibbly")}&body=${encodeURIComponent(
        `Get 5% off your first order with Dibbly — sign up through my link: ${link}`
      )}`
    : undefined

  const bounty = Number(referral?.bountyPoints ?? 2000)
  const discount = Number(referral?.friendDiscountPct ?? 5)
  const earnedPoints = Number(referralEarned?.points ?? 0)
  const earnedCount = Number(referralEarned?.count ?? 0)

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <CardHead icon={Users} title="Refer a friend" />

      <div className="px-5 py-[18px]">
        <p className="mb-[15px] text-[13px] leading-[1.55] text-muted-foreground">
          Your friends get <b className="font-bold text-foreground">{discount}% off their first order</b>. You earn{" "}
          <b className="font-bold text-foreground">
            {formatPoints(bounty)} points — worth {formatMoney(bounty / 200)}
          </b>{" "}
          — the moment they place it.
        </p>

        {/* P8-H7 "Muster" — same band treatment as the Profile card's twin of this
            box: the Copy control keeps its intrinsic width, so in the starved
            861–1023 band the link collapsed to "http:". Link on its own line. */}
        <div
          data-testid="referral-box"
          className="mb-[9px] flex items-center gap-2 rounded-[10px] border border-dashed border-line-2 bg-accent px-3 py-[10px] min-[861px]:max-[1024px]:flex-wrap min-[861px]:max-[1024px]:gap-y-2"
        >
          <span
            data-testid="referral-link"
            title={link ?? undefined}
            className="min-w-0 flex-1 overflow-hidden font-display text-[13px] font-semibold text-ellipsis whitespace-nowrap text-foreground min-[861px]:max-[1024px]:basis-full"
          >
            {link ?? "Your referral link will appear here."}
          </span>
          <button
            type="button"
            onClick={() => copyLink("Referral link copied")}
            disabled={!link}
            className="inline-flex shrink-0 items-center gap-[6px] text-[12px] font-bold text-primary disabled:opacity-50"
          >
            {copied ? <Check className="size-[14px]" strokeWidth={2.2} /> : <Copy className="size-[14px]" strokeWidth={2.2} />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>

        <p className="mt-[2px] text-[11.5px] leading-[1.5] text-ink-3">
          Friends sign up through your link — no code to enter. The reward applies automatically once they order.
        </p>

        {/* `.ref-share` — MOBILE-PLAN's "≤700 hero/how/SHARE strips → 1-col".
            This strip had no responsive treatment at all: three bordered buttons
            stayed side by side down to 365px.

            RV-101 — and it needs the SAME one-column shape inside the starved
            861–1023 band. Moving `.rw-grid` to 861 drops this card into a ~181px
            column, taking each button from 173px at 860 to 55–77px; "Copy link"
            wrapped to two lines and, because a flex item shrinks below its
            declared size unless told otherwise, the `size-[15px]` icons were
            crushed to ZERO width — three bare text buttons with no icons at all
            from 861 to ~900. One column gives them room; the band-scoped
            `shrink-0` fixes the actual root cause, since an icon that cannot
            shrink cannot vanish. Band-scoped ONLY: from 1024 up the icons measure
            10–14.6px — squeezed but visible, and that is how the app shipped
            before this slice, so widening them there would push the button
            content out and change layout this slice does not own. */}
        <div
          data-testid="rewards-share"
          className="mt-[11px] grid grid-cols-1 gap-2 min-[701px]:grid-cols-3 min-[861px]:max-[1024px]:grid-cols-1"
        >
          <a
            href={mailto}
            aria-disabled={!link}
            className="inline-flex items-center justify-center gap-[7px] rounded-[9px] border border-line-2 bg-card px-2 py-[9px] text-[12px] font-semibold text-muted-foreground no-underline transition-colors hover:bg-secondary hover:text-foreground aria-disabled:pointer-events-none aria-disabled:opacity-50"
          >
            <Mail className="size-[15px] min-[861px]:max-[1024px]:shrink-0" strokeWidth={2} />
            Email
          </a>
          <button
            type="button"
            onClick={() => copyLink("Referral link copied")}
            disabled={!link}
            className="inline-flex items-center justify-center gap-[7px] rounded-[9px] border border-line-2 bg-card px-2 py-[9px] text-[12px] font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
          >
            <Link2 className="size-[15px] min-[861px]:max-[1024px]:shrink-0" strokeWidth={2} />
            Copy link
          </button>
          <button
            type="button"
            onClick={shareLink}
            disabled={!link}
            className="inline-flex items-center justify-center gap-[7px] rounded-[9px] border border-line-2 bg-card px-2 py-[9px] text-[12px] font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
          >
            <Share2 className="size-[15px] min-[861px]:max-[1024px]:shrink-0" strokeWidth={2} />
            Share
          </button>
        </div>
      </div>

      {earnedCount > 0 && (
        <div className="flex items-center gap-[11px] border-t border-border bg-accent px-5 py-[13px]">
          <span className="grid size-[30px] shrink-0 place-items-center rounded-lg bg-[color-mix(in_oklab,var(--chart-4)_13%,transparent)]">
            <Sparkles className="size-[15px] text-chart-4" strokeWidth={2.1} />
          </span>
          <div className="text-[12.5px] text-muted-foreground">
            <b className="font-bold text-foreground">{formatPoints(earnedPoints)} points</b> earned from{" "}
            <b className="font-bold text-foreground">{earnedCount}</b> successful referral
            {earnedCount === 1 ? "" : "s"}
          </div>
        </div>
      )}
    </div>
  )
}

function FriendPill({ status }) {
  const redeemed = status === "redeemed"
  return (
    <span
      data-testid="friend-pill"
      className={
        redeemed
          ? "inline-flex shrink-0 items-center gap-[5px] rounded-full bg-[color-mix(in_oklab,var(--done)_13%,transparent)] px-[9px] py-[3px] text-[10.5px] font-bold text-done min-[861px]:max-[1024px]:ml-auto"
          : "inline-flex shrink-0 items-center gap-[5px] rounded-full bg-secondary px-[9px] py-[3px] text-[10.5px] font-bold text-muted-foreground min-[861px]:max-[1024px]:ml-auto"
      }
    >
      {redeemed ? <Check className="size-[11px]" strokeWidth={2.6} /> : <span className="size-[6px] rounded-full bg-current" />}
      {redeemed ? "Reward earned" : "Pending"}
    </span>
  )
}

function friendInitials(name) {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean)
  const letters = parts.slice(0, 2).map((part) => part[0]).join("")
  return letters.toUpperCase() || "?"
}

function FriendsCard({ referrals }) {
  const empty = referrals.length === 0

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <CardHead icon={Gift} title="Friends you've referred" meta={empty ? null : `${referrals.length} total`} />
      {empty ? (
        <EmptyBlock
          icon={Users}
          title="No referrals yet"
          body="Share your link above — friends you refer show up here once they sign up."
        />
      ) : (
        // P8-H7 "Muster" — RV-001, the blocker. The status pill is `shrink-0` at
        // ~124px and the name/meta block is `min-w-0 flex-1`, so in the starved
        // 861–1023 band (this card sits in a ~222px column while the 224px shell
        // rail is still on screen) the block was squeezed to ZERO: the friend's
        // name vanished entirely and the pill painted on top of the meta text. The
        // receipt row's two-line stack fixes it — name + meta take line 1 outright,
        // avatar and pill share line 2 — and it is applied across the WHOLE band,
        // not just the sub-range where the boxes happened to overlap.
        referrals.map((friend, i) => (
          <div
            key={`${friend.inviteeName}-${i}`}
            data-testid="friend-row"
            className="flex items-center gap-3 border-b border-border px-5 py-3 last:border-b-0 min-[861px]:max-[1024px]:flex-wrap min-[861px]:max-[1024px]:gap-y-2"
          >
            <Avatar initials={friendInitials(friend.inviteeName)} name={friend.inviteeName} size={34} />
            <div
              data-testid="friend-text"
              className="min-w-0 flex-1 min-[861px]:max-[1024px]:-order-1 min-[861px]:max-[1024px]:basis-full"
            >
              <div data-testid="friend-name" className="truncate text-[13.5px] font-semibold text-foreground">
                {friend.inviteeName}
              </div>
              <div className="mt-px text-[11.5px] text-ink-3">
                {friend.status === "redeemed" ? "Placed their first order" : "Signed up · no order yet"}
                {friend.date ? ` · ${formatDate(friend.date)}` : ""}
              </div>
            </div>
            <FriendPill status={friend.status} />
          </div>
        ))
      )}
    </div>
  )
}

export default function Rewards({ rewards }) {
  const balance = rewards?.balance ?? { points: 0, dollarsValue: 0 }
  const activity = rewards?.activity ?? []
  const referrals = rewards?.referrals ?? []
  const referral = rewards?.referral ?? {}
  const referralEarned = rewards?.referralEarned ?? { count: 0, points: 0 }

  return (
    <>
      <div className="mx-auto flex w-full max-w-[1020px] flex-col gap-5 px-[30px] pt-6 pb-16">
        <div>
          <h1 className="font-display text-[23px] font-bold tracking-[-0.5px] text-foreground">Rewards &amp; referrals</h1>
          <p className="mt-[3px] max-w-[560px] text-[13px] leading-[1.5] text-ink-3">
            Earn Dibbly Rewards points on every order and bonus points for friends you bring to Dibbly — then redeem
            them toward your next book.
          </p>
        </div>

        <BalanceHero balance={balance} lifetimeEarned={rewards?.lifetimeEarned} redeemed={rewards?.redeemed} />

        <HowYouEarn pointsPerDollar={referral.pointsPerDollar} />

        {/* `.rw-grid` is 1-col at ≤860 — 861 is the first two-column width. */}
        <div
          data-testid="rewards-grid"
          className="grid grid-cols-1 items-start gap-5 min-[861px]:grid-cols-[1.5fr_1fr]"
        >
          <div className="flex min-w-0 flex-col gap-5">
            <ActivityCard activity={activity} />
          </div>
          <div className="flex min-w-0 flex-col gap-5">
            <ReferralCard referral={referral} referralEarned={referralEarned} onToast={toast} />
            <FriendsCard referrals={referrals} />
          </div>
        </div>
      </div>
    </>
  )
}

Rewards.layout = (page) => (
  <AppShell active="Rewards" crumb={["Rewards"]}>
    {page}
  </AppShell>
)
